import styled from 'styled-components';

import { hexWithAlpha } from 'utils/colors';

export const Legend = styled.div`
  background-color: ${({ theme }) => theme.colors.surfaceSoft};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 1rem;
  padding: 1.2rem 1.4rem;
  margin-bottom: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

export const LegendTitle = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.darkSlate};
  letter-spacing: 0.05rem;
  text-transform: uppercase;
`;

export const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
`;

export const LegendText = styled.span`
  font-size: 1.25rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.slateLight};
`;

export const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const FilterSearch = styled.input`
  flex: 1;
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 1rem;
  padding: 0.8rem 1.2rem;
  font-size: 1.3rem;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.darkSlate};
  outline: none;
`;

export const FilterSelect = styled.select`
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 1rem;
  padding: 0.8rem 1.2rem;
  font-size: 1.3rem;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.darkSlate};
  background-color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
`;

export const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-bottom: 1.6rem;
`;

export const Chip = styled.button<{ $selected: boolean; $accent: string }>`
  border: 0.1rem solid
    ${({ $selected, $accent, theme }) =>
      $selected ? $accent : theme.colors.border};
  border-radius: 1.6rem;
  padding: 0.6rem 1.2rem;
  font-size: 1.2rem;
  font-weight: ${({ $selected }) => ($selected ? 700 : 600)};
  color: ${({ $selected, $accent, theme }) =>
    $selected ? theme.colors.white : theme.colors.slateGrey};
  background-color: ${({ $selected, $accent, theme }) =>
    $selected ? $accent : theme.colors.white};
  cursor: pointer;
  font-family: inherit;
`;

export const EditLinkRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.8rem;
`;

export const EditLink = styled.a<{ $accent: string }>`
  font-size: 1.2rem;
  color: ${({ $accent }) => $accent};
  text-decoration: none;
  font-weight: 600;
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

export const Card = styled.div<{ $group?: boolean; $accent?: string }>`
  border: ${({ $group, $accent, theme }) =>
    $group
      ? `0.15rem solid ${$accent ? hexWithAlpha($accent, 0.4) : theme.colors.accentBorder}`
      : `0.1rem solid ${theme.colors.border}`};
  border-radius: 1.2rem;
  padding: ${({ theme }) => theme.spacing.md};
`;

export const GroupBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const GroupBadge = styled.span<{ $accent: string }>`
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.05rem;
  color: ${({ $accent }) => $accent};
  background: ${({ theme }) => theme.colors.accentBgSoft};
  border-radius: 0.6rem;
  padding: 0.2rem 0.6rem;
`;

export const ParticipantsLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.04rem;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.steelGrey};
  margin-top: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const ParticipantList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

export const ParticipantRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm}
    ${({ theme }) => theme.spacing.md};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 0.8rem;
  background: ${({ theme }) => theme.colors.white};
`;

export const ParticipantInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  flex-shrink: 1;
`;

export const ParticipantName = styled.span`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const ParticipantRole = styled.span`
  font-size: 1.15rem;
  color: ${({ theme }) => theme.colors.steelGrey};
`;

export const ServiceName = styled.div`
  font-size: 1.3rem;
  font-style: italic;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.darkSlate};
  margin-bottom: 0.6rem;
`;

export const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const Name = styled.span`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

export const Organisation = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.slateGrey};
  margin-left: 0.6rem;
`;

export const Date = styled.span`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.slateGrey};
`;

export const Badge = styled.span<{ $bg: string; $color: string }>`
  display: inline-block;
  margin-left: 0.8rem;
  background-color: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  font-size: 1.1rem;
  font-weight: 700;
  padding: 0.2rem 0.8rem;
  border-radius: 1rem;
  vertical-align: middle;
`;

export const EditedChip = styled.span`
  display: inline-block;
  margin-left: 0.8rem;
  background-color: ${({ theme }) => theme.colors.amberBgSoft};
  color: ${({ theme }) => theme.colors.amberText};
  font-size: 1.1rem;
  font-weight: 700;
  padding: 0.2rem 0.8rem;
  border-radius: 1rem;
  vertical-align: middle;
`;

export const RatingDelta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.6rem;
  margin-bottom: 0.6rem;
  font-size: 1.4rem;
  flex-wrap: wrap;
`;

export const RatingStars = styled.span`
  color: ${({ theme }) => theme.colors.amber};
  letter-spacing: 0.1rem;
`;

export const RatingArrow = styled.span`
  color: ${({ theme }) => theme.colors.slateGrey};
  font-size: 1.3rem;
  font-weight: 700;
`;

export const RatingLabel = styled.span`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.slateLight};
  font-weight: 600;
`;

export const UpdatedChip = styled.span`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.amberText};
  background-color: ${({ theme }) => theme.colors.amberBgSoft};
  padding: 0.2rem 0.6rem;
  border-radius: 0.8rem;
  font-weight: 700;
  margin-left: 0.2rem;
`;

export const OriginalComment = styled.div`
  font-size: 1.3rem;
  font-style: italic;
  color: ${({ theme }) => theme.colors.slateMuted};
  margin-top: 0.6rem;
  text-decoration: line-through;
`;

export const CommentLabel = styled.span`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.slateLight};
  font-style: normal;
`;

export const Achievement = styled.div`
  margin-top: 0.8rem;
  padding: 0.8rem 1.2rem;
  border-radius: 1rem;
  background: ${({ theme }) => theme.colors.accentBgSoft};
  border: 0.1rem solid ${({ theme }) => theme.colors.accentBorder};
  color: ${({ theme }) => theme.colors.darkSlate};
  font-size: 1.3rem;
  font-weight: 600;
`;

export const AchievementTimeframe = styled.span`
  color: ${({ theme }) => theme.colors.slate};
  font-weight: 500;
  margin-left: 0.6rem;
`;

export const Comment = styled.div`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  margin-top: ${({ theme }) => theme.spacing.xs};
  line-height: 1.5;
`;

export const ProviderResponseBox = styled.div<{ $accent?: string }>`
  margin-top: 1.2rem;
  margin-left: 2.4rem;
  padding: 1rem 1.2rem;
  border-radius: 1rem;
  background: ${({ $accent, theme }) =>
    $accent ? `${$accent}14` : theme.colors.tealBgSoft};
  border-left: 0.3rem solid
    ${({ $accent, theme }) => $accent || theme.colors.teal};

  @media (max-width: 600px) {
    margin-left: 1.2rem;
  }
`;

export const ProviderResponseLabel = styled.div<{ $accent?: string }>`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ $accent, theme }) => $accent || theme.colors.teal};
  text-transform: uppercase;
  letter-spacing: 0.05rem;
  margin-bottom: 0.4rem;
`;

export const ProviderResponseBody = styled.div`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.darkSlate};
  line-height: 1.5;
`;

export const ProviderResponseMeta = styled.div`
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.slateLight};
  margin-top: 0.4rem;
`;

export const ImprovementBox = styled.div`
  margin-top: 0.8rem;
  padding-left: 1.2rem;
  border-left: 0.3rem solid ${({ theme }) => theme.colors.amberSolid};
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
`;

export const ImprovementLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.slateLight};
  text-transform: uppercase;
  letter-spacing: 0.05rem;
  margin-bottom: 0.2rem;
`;

export const ImprovementBody = styled.div`
  font-size: 1.3rem;
  font-style: italic;
  color: ${({ theme }) => theme.colors.slateLight};
  line-height: 1.5;
`;

export const MediaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.8rem;
`;

export const Thumb = styled.div`
  position: relative;
  width: 6.4rem;
  height: 6.4rem;
  border-radius: 0.6rem;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.surface};
  display: block;
  cursor: pointer;
`;

export const Media = styled.div`
  width: 100%;
  height: 100%;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const PlayBadge = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.4rem;
  text-shadow: 0 0.1rem 0.2rem rgba(0, 0, 0, 0.6);
`;

export const AudioCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  height: 6.4rem;
  width: 28rem;
  max-width: 100%;
  border-radius: 0.6rem;
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.surfaceSoft};
  padding: 0 0.8rem;
`;

export const AudioIcon = styled.span`
  font-size: 1.6rem;
  flex-shrink: 0;
`;

export const AudioPlayer = styled.audio`
  width: 100%;
  height: 3.2rem;
`;

export const ContactRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 0.1rem solid ${({ theme }) => theme.colors.border};
`;

export const ContactLabel = styled.span<{ $accent: string }>`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ $accent }) => $accent};
`;

export const ContactButton = styled.button<{ $accent: string }>`
  display: inline-block;
  width: fit-content;
  padding: 0.6rem 1.4rem;
  border-radius: 1.6rem;
  background-color: ${({ $accent }) => `${$accent}1F`};
  color: ${({ $accent }) => $accent};
  font-size: 1.2rem;
  font-weight: 700;
  border: 0.1rem solid ${({ $accent }) => `${$accent}52`};
  cursor: pointer;
  font-family: inherit;
`;

export const ContactPrivacyNote = styled.span`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.slateMuted};
`;

export const LoadMore = styled.button<{ $accent: string }>`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 1rem;
  background: none;
  font-size: 1.4rem;
  font-weight: 500;
  color: ${({ $accent }) => $accent};
  cursor: pointer;
`;

export const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const LightboxContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LightboxClose = styled.button`
  position: absolute;
  top: -4rem;
  right: 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  font-size: 2.2rem;
  cursor: pointer;
  line-height: 1;
`;

export const LightboxMedia = styled.div`
  max-width: 90vw;
  max-height: 85vh;

  img,
  video {
    max-width: 90vw;
    max-height: 85vh;
    border-radius: 0.8rem;
    object-fit: contain;
  }
`;

export const InquiryOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.4rem;
  z-index: 1100;
`;

export const InquiryDialog = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 1.6rem;
  padding: 2.4rem;
  max-width: 44rem;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

export const InquiryClose = styled.button`
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  background: none;
  border: none;
  font-size: 1.8rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  cursor: pointer;
`;

export const InquiryTitle = styled.div`
  font-size: 1.8rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
  margin-bottom: 0.6rem;
`;

export const InquirySubtitle = styled.div`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  line-height: 1.5;
  margin-bottom: 1.6rem;
`;

export const InquiryPromptsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 1.4rem;
`;

export const InquiryPrompt = styled.button<{
  $selected: boolean;
  $accent: string;
}>`
  padding: 0.6rem 1.2rem;
  border-radius: 1.6rem;
  border: 0.1rem solid
    ${({ $selected, $accent, theme }) =>
      $selected ? $accent : theme.colors.border};
  background-color: ${({ $selected, $accent, theme }) =>
    $selected ? `${$accent}14` : theme.colors.surfaceSoft};
  color: ${({ $selected, $accent, theme }) =>
    $selected ? $accent : theme.colors.slate};
  font-size: 1.2rem;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  cursor: pointer;
  font-family: inherit;
`;

export const InquiryPrivacyNote = styled.div<{ $accent: string }>`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  background-color: ${({ $accent }) => `${$accent}14`};
  border: 0.1rem solid ${({ $accent }) => `${$accent}33`};
  border-radius: 1rem;
  padding: 1.2rem 1.4rem;
  margin-bottom: 1.4rem;
`;

export const InquiryPrivacyIcon = styled.span`
  font-size: 1.8rem;
  flex-shrink: 0;
  line-height: 1.2;
`;

export const InquiryPrivacyTitle = styled.div<{ $accent: string }>`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${({ $accent }) => $accent};
  margin-bottom: 0.2rem;
`;

export const InquiryPrivacyBody = styled.div<{ $accent: string }>`
  font-size: 1.2rem;
  color: ${({ $accent }) => $accent};
  line-height: 1.4;
`;

export const InquiryError = styled.div`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.error};
  margin-bottom: 0.8rem;
`;

export const InquirySendBtn = styled.button<{ $accent: string }>`
  width: 100%;
  border: none;
  border-radius: 1.2rem;
  padding: 1.2rem 1.6rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ $accent }) => $accent};
  cursor: pointer;
  &:disabled {
    background-color: ${({ theme }) => theme.colors.disabled};
    cursor: not-allowed;
  }
`;

export const InquirySent = styled.div`
  text-align: center;
  padding: 2.4rem 0;
`;

export const InquirySentIcon = styled.div`
  font-size: 3.2rem;
  margin-bottom: 0.8rem;
`;

export const InquirySentTitle = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.successDark};
  margin-bottom: 0.8rem;
`;

export const InquirySentBody = styled.div`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.slateGrey};
`;
